import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, mergeProps as _mergeProps, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps } from "vue";
const _hoisted_1 = { class: "alert alert-info alert-small" };
import { computed, ref, watch, watchEffect, useSlots } from "vue";
import { camelToKebab } from "@/common/helpers/strings";
import { vTooltip } from "@/common/directives/tip-directive";
// Components
import SvgIcon from "@/common/components/SvgIcon.vue";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
// Setup
export default /*@__PURE__*/ _defineComponent({
    __name: 'RecTable',
    props: /*@__PURE__*/ _mergeModels({
        items: {},
        headers: {},
        loading: { type: Boolean },
        idKey: { default: "id" },
        multiple: { type: Boolean },
        rowsPerPage: { default: 25 },
        rowsPerPageOptions: { default: () => [5, 10, 25, 50] },
        noData: { default: "" },
        noScrollOnPageUpdate: { type: Boolean },
        paginator: { type: Boolean, default: true },
        headless: { type: Boolean },
        rowStyle: { default: undefined },
        rowTooltip: { type: Function, default: () => "" }
    }, {
        "expandedRows": {},
        "expandedRowsModifiers": {},
        "selection": { default: () => [] },
        "selectionModifiers": {},
        "sort": {},
        "sortModifiers": {},
        "page": { default: () => ({ first: 0, page: 0 }) },
        "pageModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["row-click"], ["update:expandedRows", "update:selection", "update:sort", "update:page"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // Helper functions
        const getItemId = (item) => item[props.idKey];
        // Table
        const el = ref();
        const filteredHeaders = computed(() => props.headers.filter(({ condition }) => typeof condition !== "function" || condition()));
        const tableTop = ref(0);
        watch(el, () => {
            if (!el.value)
                return;
            tableTop.value = el.value.$el.getBoundingClientRect().top;
        });
        const expandedRows = _useModel(__props, "expandedRows");
        const selectionModel = _useModel(__props, "selection");
        const selectionModelMap = computed(() => new Map(selectionModel.value.reduce((acc, item) => [...acc, [getItemId(item), item]], [])));
        const optionsMap = computed(() => new Map(props.items.reduce((acc, item) => [...acc, [getItemId(item), item]], [])));
        // ...sorting
        const sortModel = _useModel(__props, "sort");
        const onSort = ({ sortField: field, sortOrder: order }) => {
            sortModel.value = !field || !order ? undefined : { field, order };
        };
        // ...pagination
        const pageModel = _useModel(__props, "page");
        const pageItems = computed(() => props.items.slice(pageModel.value.first, pageModel.value.first + props.rowsPerPage));
        const onPage = ({ first, page }) => {
            pageModel.value = { first, page };
            if (props.noScrollOnPageUpdate)
                return;
            window.scrollTo({ top: tableTop.value, behavior: "smooth" });
        };
        // ...row clicking
        const isPlainClick = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
        const onRowClick = ({ data, originalEvent: e }) => {
            if (!(e instanceof MouseEvent) || !isPlainClick(e))
                return;
            // Don't treat clicks on interactive cell content (links, buttons, form controls) as row activations.
            if (e.target instanceof Element && e.target.closest('a, button, input, select, textarea, label, [role="button"]'))
                return;
            emit("row-click", e, data);
        };
        // ...shift+click items selection
        const mostRecentSelectionIndex = ref();
        const onRowSelect = ({ originalEvent, index }) => {
            if (mostRecentSelectionIndex.value !== undefined && originalEvent.shiftKey) {
                const minVal = Math.min(mostRecentSelectionIndex.value, index) + pageModel.value.first;
                const maxVal = Math.max(mostRecentSelectionIndex.value, index) + 1 + pageModel.value.first;
                selectionModel.value = [
                    ...selectionModel.value,
                    ...props.items.slice(minVal, maxVal).filter((item) => selectionModel.value.indexOf(item) === -1)
                ];
            }
            mostRecentSelectionIndex.value = index;
        };
        // ...selection
        const selectAll = ref(false);
        const isItemSelected = (item) => selectionModelMap.value.has(getItemId(item));
        // Set selectAll to true if all props.items are referenced in selectionModel.
        //  Using selectionModelMap map prevents from iterating on selectionModel.
        watchEffect(() => {
            selectAll.value = props.items.length > 0 && props.items.every(isItemSelected);
        });
        const onSelectAllChange = ({ checked }) => {
            selectAll.value = checked;
            selectionModel.value = checked
                ? // On select all, add all available (yet not selected) options to current selection
                    [...selectionModel.value, ...props.items.filter((item) => !isItemSelected(item))]
                : // On unselect all, remove all selectionModel item referenced in prop.items.
                    // Using optionsMap prevents from iterating on props.items.
                    selectionModel.value.filter((item) => !optionsMap.value.has(getItemId(item)));
        };
        // ...column widths
        const columnWidths = ref(Array(props.headers.length).fill(0));
        const { expansion } = useSlots();
        // only watch if expansion slot is provided since columnWidths is meant to be used only within the expansion slot
        if (expansion) {
            watch(() => !props.loading && el.value?.$el, (next) => {
                if (!next)
                    return;
                columnWidths.value = [...next.querySelectorAll("th")].map(({ clientWidth }) => clientWidth);
            });
        }
        __expose({
            columnWidths
        });
        // PrimeVue DataTable customization
        const pt = computed(() => ({
            root: "rec-data-table",
            thead: {
                style: props.headless ? "display: none;" : undefined
            },
            bodyRow: ({ context }) => ({
                id: context?.index !== undefined
                    ? `${camelToKebab(props.idKey)}-${props.items[context.index + pageModel.value.first][props.idKey]}`
                    : undefined,
                class: "p-datatable__row"
            }),
            headerRow: "p-datatable__row",
            rowExpansionCell: "p-datatable__row-expansion",
            column: {
                bodyCell: "p-datatable__body-cell",
                headerCell: "p-datatable__header-cell",
                headerContent: ({ props: scopedProps }) => {
                    const headers = props.headers.reduce((acc, h) => ({ ...acc, [h.field]: h }), {});
                    const justify = headers[scopedProps.field]?.justify;
                    return {
                        class: { [`justify-content-${justify ?? "start"}`]: !!justify }
                    };
                },
                headerCheckboxWrapper: "p-checkbox-box-wrapper",
                headerCheckbox: ({ context }) => ({
                    class: [
                        {
                            "p-checkbox-box--focused": context?.focused,
                            "p-checkbox-box--checked": context?.checked || selectAll.value
                        }
                    ]
                }),
                checkboxWrapper: "p-checkbox-box-wrapper",
                checkbox: ({ context }) => ({
                    class: [
                        {
                            "p-checkbox-box--focused": context?.focused,
                            "p-checkbox-box--checked": context?.checked || selectAll.value
                        }
                    ]
                }),
                rowToggler: ({ instance: { rowData } }) => ({
                    class: "row-expander",
                    disabled: !rowData.expandable
                })
            },
            paginator: {
                firstPageButton: ({ context }) => ({
                    class: { ["p-paginator-first--hidden"]: !!context?.disabled }
                }),
                previousPageButton: ({ context }) => ({
                    class: { ["p-paginator-prev--hidden"]: !!context?.disabled }
                }),
                nextPageButton: ({ context }) => ({
                    class: { ["p-paginator-next--hidden"]: !!context?.disabled }
                }),
                lastPageButton: ({ context }) => ({
                    class: { ["p-paginator-last--hidden"]: !!context?.disabled }
                }),
                pageButton: ({ context }) => ({
                    class: { "p-paginator-page--active": context?.active }
                })
            }
        }));
        const getSortIconClass = (sorted) => [
            "p-datatable__sort-direction-icon",
            {
                "p-datatable__sort-direction-icon--unsorted": !sorted,
                [`p-datatable__sort-direction-icon--${sortModel.value?.order === -1 ? "desc" : "asc"}`]: sorted && !!sortModel.value?.order && [-1, 1].includes(sortModel.value.order)
            }
        ];
        const getBodyCellClass = (header) => [
            "p-datatable__body-cell__inner",
            {
                [`justify-content-${header.justify ?? "start"}`]: true
            }
        ];
        const getScopeProps = (scopeProps) => {
            const sp = scopeProps;
            return {
                ...sp,
                item: sp.data[sp.field]
            };
        };
        const getBodyContent = (scopeProps) => {
            const { data, field } = scopeProps;
            return data[field] ? data[field] : undefined;
        };
        const getRowTooltip = (data) => {
            return props.rowTooltip?.(data);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(DataTable), {
                ref_key: "el",
                ref: el,
                selection: selectionModel.value,
                "onUpdate:selection": _cache[0] || (_cache[0] = ($event) => ((selectionModel).value = $event)),
                expandedRows: expandedRows.value,
                "onUpdate:expandedRows": _cache[1] || (_cache[1] = ($event) => ((expandedRows).value = $event)),
                value: pageItems.value,
                "data-key": __props.idKey,
                "total-records": __props.items.length,
                first: pageModel.value.first,
                rows: __props.rowsPerPage,
                "rows-per-page-options": __props.rowsPerPageOptions,
                "select-all": selectAll.value,
                "sort-field": sortModel.value?.field,
                "sort-order": sortModel.value?.order,
                "row-style": () => __props.rowStyle,
                loading: __props.loading,
                pt: pt.value,
                paginator: __props.paginator,
                "paginator-template": "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
                lazy: "",
                onPage: onPage,
                onSort: onSort,
                onSelectAllChange: onSelectAllChange,
                onRowSelect: onRowSelect,
                onRowClick: onRowClick
            }, {
                empty: _withCtx(() => [
                    _renderSlot(_ctx.$slots, "no-data", {}, () => [
                        _createElementVNode("div", _hoisted_1, [
                            _createVNode(SvgIcon, {
                                class: "rc-icon-information",
                                icon: "icon-information"
                            }),
                            _createTextVNode(" " + _toDisplayString(__props.noData), 1)
                        ])
                    ])
                ]),
                expansion: _withCtx((slotProps) => [
                    _renderSlot(_ctx.$slots, "expansion", _normalizeProps(_guardReactiveProps(slotProps)))
                ]),
                default: _withCtx(() => [
                    (_ctx.$slots.expansion)
                        ? (_openBlock(), _createBlock(_unref(Column), {
                            key: 0,
                            expander: ""
                        }, {
                            rowtogglericon: _withCtx(({ rowExpanded }) => [
                                _createVNode(SvgIcon, {
                                    class: _normalizeClass(['chevron-icon', { 'chevron-icon--active': rowExpanded }]),
                                    icon: "icon-arrow-down",
                                    height: "0.75rem",
                                    width: "0.75rem"
                                }, null, 8, ["class"])
                            ]),
                            _: 1
                        }))
                        : _createCommentVNode("", true),
                    (__props.multiple)
                        ? (_openBlock(), _createBlock(_unref(Column), {
                            key: 1,
                            "selection-mode": "multiple",
                            class: "p-datatable__selection-column"
                        }, {
                            rowcheckboxicon: _withCtx(({ checked }) => [
                                checked
                                    ? (_openBlock(), _createBlock(SvgIcon, {
                                        key: 0,
                                        color: "white",
                                        icon: "icon-checkbox",
                                        height: "0.875rem",
                                        width: "0.875rem"
                                    }))
                                    : _createCommentVNode("", true)
                            ]),
                            headercheckboxicon: _withCtx(({ checked }) => [
                                checked
                                    ? (_openBlock(), _createBlock(SvgIcon, {
                                        key: 0,
                                        color: "white",
                                        icon: "icon-checkbox",
                                        height: "0.875rem",
                                        width: "0.875rem"
                                    }))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 1
                        }))
                        : _createCommentVNode("", true),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredHeaders.value, (header, i) => {
                        return (_openBlock(), _createBlock(_unref(Column), {
                            key: `header-${i}`,
                            field: header.field,
                            "header-class": header.class,
                            class: _normalizeClass(header.class),
                            style: _normalizeStyle(header.style),
                            sortable: header.sortable
                        }, {
                            header: _withCtx(() => [
                                _renderSlot(_ctx.$slots, `header-${header.field}`, _mergeProps({ ref_for: true }, { item: header }), () => [
                                    _createTextVNode(_toDisplayString(header.title), 1)
                                ])
                            ]),
                            sorticon: _withCtx(({ sorted }) => [
                                _createVNode(SvgIcon, {
                                    icon: "icon-triangle",
                                    class: _normalizeClass(getSortIconClass(sorted))
                                }, null, 8, ["class"])
                            ]),
                            body: _withCtx((scopeProps) => [
                                _withDirectives((_openBlock(), _createElementBlock("div", {
                                    class: _normalizeClass(getBodyCellClass(header))
                                }, [
                                    _renderSlot(_ctx.$slots, scopeProps.field, _mergeProps({ ref_for: true }, getScopeProps(scopeProps)), () => [
                                        _createTextVNode(_toDisplayString(getBodyContent(scopeProps)), 1)
                                    ])
                                ], 2)), [
                                    [
                                        _unref(vTooltip),
                                        getRowTooltip(scopeProps.data),
                                        void 0,
                                        {
                                            pointer: true,
                                            parent: true
                                        }
                                    ]
                                ])
                            ]),
                            _: 2
                        }, 1032, ["field", "header-class", "class", "style", "sortable"]));
                    }), 128))
                ]),
                _: 3
            }, 8, ["selection", "expandedRows", "value", "data-key", "total-records", "first", "rows", "rows-per-page-options", "select-all", "sort-field", "sort-order", "row-style", "loading", "pt", "paginator"]));
        };
    }
});
